/** @file
 *  Contains the ScatterPlot component used to plot data used for Pro visualisations.
 */

import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';

import Scatter from './Scatter';
import {
    convertSkillToTitleCase,
    getSkillsActiveDotStyle,
    getSkillsInactiveDotStyle
} from '../../functions/dataFormatting';
import { VictoryLabel } from 'victory';
import DrillDownTooltip from '../tooltips/DrillDownTooltip';
import { useTooltip } from '../../hooks/tooltip';
import {
    calculateDx,
    calculateDy,
    calculateTextAnchor,
    calculateVertAnchor
} from '../../functions/labels';
import { useDesktopChartEventHandlers } from '../../hooks/eventHandlers';

/**
 * Renders a scatter plot used for Pro visualisations with drill-down functionality.
 * It displays data points representing skills, and it allows users to hover over or click on data points to view detailed information.
 *
 * @component
 * @param {array} data - the data array containing information about skills and their attributes
 * @returns {JSX.Element}
 */
export const DrillDownScatter = ({ data }) => {
    const theme = useTheme();
    const { tooltipState, setTooltipState, clearTooltip } = useTooltip();

    const isAboveSmallScreenSize = useMediaQuery((theme) =>
        theme.breakpoints.up('sm')
    );

    const { onMouseOverDot, onMouseOutDot } = useDesktopChartEventHandlers(
        { tooltipState, setTooltipState, clearTooltip },
        {
            getActiveDotStyle: getSkillsActiveDotStyle,
            getInactiveDotStyle: getSkillsInactiveDotStyle
        }
    );

    const labels = useMemo(() => {
        if (data) {
            return ({ datum }) => convertSkillToTitleCase(datum?.skill);
        } else {
            return undefined;
        }
    }, [data]);

    const labelComponent = useMemo(() => {
        if (data) {
            return (
                <VictoryLabel
                    dx={({ datum }) => calculateDx(datum)}
                    dy={({ datum }) => calculateDy(datum)}
                    textAnchor={({ datum }) => calculateTextAnchor(datum)}
                    verticalAnchor={({ datum }) => calculateVertAnchor(datum)}
                />
            );
        } else {
            return undefined;
        }
    }, [data]);

    return (
        <>
            <Scatter
                data={data}
                dataTestId={'skillsScatter'}
                dataAnalyticsId={'portfolio-drilldown-scatter-plot'}
                labels={labels}
                labelComponent={labelComponent}
                onMouseOver={(event, data, graphRef) => {
                    return onMouseOverDot(event, data, graphRef);
                }}
                onMouseOut={onMouseOutDot}
                onClick={(event) => {
                    event.stopPropagation(); //prevent tooltip removal on page click
                }}
                size={theme.sizing.scatterPlot.skillsPoints.radius.initial}
                style={{
                    data: {
                        strokeWidth:
                            theme.sizing.scatterPlot.skillsPoints.stroke
                                .initial,
                        stroke: theme.palette.scatterPlot.skillsPoints.stroke
                            .initial,
                        fill: ({ datum }) => datum.dotColor
                    },
                    labels: {
                        fontFamily: theme.typography.fontFamily,
                        fontSize: 10 // can't be rem
                    }
                }}
            />
            {isAboveSmallScreenSize &&
                (tooltipState.hovered || tooltipState.selected) && (
                    <DrillDownTooltip
                        position={{
                            x: tooltipState.posX,
                            y: tooltipState.posY
                        }}
                        dotData={tooltipState.selectedDot}
                    />
                )}
        </>
    );
};

DrillDownScatter.propTypes = {
    data: PropTypes.array
};
